import React, { Fragment, memo } from "react";
import { parseString } from "@repo/lib/parseString";
import { cn } from "@repo/shared/utils";

const parseMarkdown = (
  markup: string,
  styles: Record<string, string | boolean>,
  props: Record<string, object>,
  { newLines = true }: { newLines?: boolean }
) => {
  const match = parseString(markup.replace(/\r/g, ""));

  // wrap the contents in a React node matching the given wrapper type
  const getProps = (tag: string, className?: string) =>
    styles[tag] === false
      ? null
      : { ...(props[tag] ?? {}), className: cn(className, styles[tag]) };

  const wrap = (type: string, contents: React.ReactNode, key: number) => {
    const props = (tag: string, className?: string) => ({
      ...getProps(tag, className),
      key,
    });

    switch (type) {
      case "__": {
        const p = props("u", /*@tw*/ "underline");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "~~": {
        const p = props("del");
        if (p) contents = <del {...p}>{contents}</del>;
        break;
      }
      case "*": {
        const p = props("em");
        if (p) contents = <em {...p}>{contents}</em>;
        break;
      }
      case "**": {
        const p = props("strong");
        if (p) contents = <strong {...p}>{contents}</strong>;
        break;
      }
      case "***": {
        const pe = props("em");
        if (pe) contents = <em {...pe}>{contents}</em>;
        const ps = props("strong");
        if (ps) contents = <strong {...ps}>{contents}</strong>;
        break;
      }
      case "`":
      case "``": {
        const p = props("code");
        if (p) contents = <code {...p}>{contents}</code>;
        break;
      }
      case "```": {
        const p = props("pre");
        if (p) contents = <pre {...p}>{contents}</pre>;
        break;
      }
      case "#": {
        const p = props("h1", /*@tw*/ "text-3xl");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "##": {
        const p = props("h2", /*@tw*/ "text-2xl");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "###": {
        const p = props("h3", /*@tw*/ "text-xl");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "####": {
        const p = props("h4", /*@tw*/ "text-lg");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "#####": {
        const p = props("h5", /*@tw*/ "text-sm");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      case "######": {
        const p = props("h6", /*@tw*/ "text-sm");
        if (p) contents = <span {...p}>{contents}</span>;
        break;
      }
      default: {
        return (
          <Fragment key={key}>
            {type}
            {contents}
            {type}
          </Fragment>
        );
      }
    }
    return contents;
  };
  const anchor = (contents: React.ReactNode, url: string, key: number) => {
    const p = getProps("a");
    return (
      <a {...p} key={key} href={url}>
        {contents}
      </a>
    );
  };

  const modes = {
    main(watchFor: string | null = null): React.ReactNode[] {
      const inCode = this.isCodeType(watchFor);
      const parts: React.ReactNode[] = [];

      const pushString = (str: string) => {
        if (typeof parts[parts.length - 1] === "string") {
          parts[parts.length - 1] += str;
        } else {
          parts.push(str);
        }
      };

      while (match.index < match.source.length) {
        // if this is the closer of the current context, step back up
        if (watchFor && match(watchFor, true)) break;

        // if not in a code block, search for nested text formatting
        if (!inCode) {
          // search for nested wrappers / headers / links
          const key = parts.length;
          const wrapper =
            this.wrapper(key) ||
            this.header(key) ||
            this.anchor(key) ||
            this.simpleAnchor(key);

          if (wrapper) {
            parts.push(wrapper);
            continue;
          }
        }

        // if an opening wrapper matches here, it failed its closer; treat as plaintext
        const failedOpener = this.matchWrapper() || this.matchHashes();
        if (failedOpener) {
          parts.push(failedOpener);
          continue;
        }

        // match newline or new paragraph
        const newLine = this.matchNewline();
        if (newLine) {
          if (this.matchNewline(true) && this.isInlineType(watchFor)) {
            break; // we're watching for an inline wrapper, but hit a new paragraph: fail the match
          }

          parts.push(newLines ? <br key={parts.length} /> : " ");
          continue;
        }

        // match escaped char
        const char = this.matchEscapedChar();
        if (char) {
          parts.push(char);
          continue;
        }

        // match non-symbols
        const text = this.matchText();
        if (text) {
          pushString(text);
          continue;
        }

        // if all else fails, force-match next character
        pushString(match(1));
      }

      return parts;
    },
    wrapper(key: number): React.ReactNode | false {
      const revert = match.reverter();

      const opener = this.matchWrapper();
      if (!opener) return revert();

      const nodes = this.main(opener);

      const closer = this.matchWrapper();
      if (opener !== closer) return revert();

      return wrap(opener, nodes, key);
    },
    anchor(key: number): React.ReactNode | false {
      const revert = match.reverter();

      if (!match("[")) return revert();

      const nodes = this.main("]");

      if (!match("](")) return revert();

      match(/^\s+/);
      const url = match(/^[^\s\n)]+/);
      if (!url) return revert();
      match(/^\s+/);

      if (!match(")")) return revert();

      return anchor(nodes, url[0], key);
    },
    simpleAnchor(key: number): React.ReactNode | false {
      const revert = match.reverter();

      if (!match("<")) return revert();

      const url = match(/^[^\s\n>]+/);
      if (!url) return revert();

      if (!match(">")) return revert();

      return anchor(url[0], url[0], key);
    },
    header(key: number): React.ReactNode | false {
      const revert = match.reverter();

      const hashes = this.matchHashes();
      if (!hashes) return revert();

      const nodes = this.main("\n");
      return wrap(hashes, nodes, key);
    },
    matchEscapedChar(peek?: boolean): string | false {
      const m = match(/^\\[^\n]/, peek);
      return m?.[0] ?? false;
    },
    matchText(peek?: boolean): string | false {
      const m = match(/^[\da-z\t \u0080-\uffff]+/i, peek);
      return m?.[0] ?? false;
    },
    matchWrapper(peek?: boolean): string | false {
      return (
        this.matchPre(peek) ||
        this.matchUnderline(peek) ||
        this.matchTildes(peek) ||
        this.matchStars(peek) ||
        this.matchTicks(peek)
      );
    },
    matchUnderline(peek?: boolean): string | false {
      const m = match(/^__/, peek);
      return m?.[0] ?? false;
    },
    matchTildes(peek?: boolean): string | false {
      const m = match(/^~~/, peek);
      return m?.[0] ?? false;
    },
    matchStars(peek?: boolean): string | false {
      const m = match(/^\*+/, peek);
      return m?.[0] ?? false;
    },
    matchTicks(peek?: boolean): string | false {
      const m = match(/^`+/, peek);
      return m?.[0] ?? false;
    },
    matchPre(peek?: boolean): string | false {
      if (!this.atLineStart(true)) return false;
      const m = match(/^[ \t]*(```)(?=\n|$)/, peek);
      return m?.[1] ?? false;
    },
    matchHashes(peek?: boolean): string | false {
      if (!this.atLineStart(true)) return false;
      const m = match(/^[ \t]*(#+) /, peek);
      return m?.[1] ?? false;
    },
    matchNewline(peek?: boolean): string | false {
      return match("\n", peek) || false;
    },
    atLineStart(withWhitespace?: boolean): boolean {
      let index = match.index - 1;
      if (withWhitespace) {
        while (
          index >= 0 &&
          /\s/.test(match.source[index]) &&
          match.source[index] !== "\n"
        ) {
          index--;
        }
      }
      return index < 0 || match.source[index] === "\n";
    },
    isInlineType(token?: unknown): boolean {
      if (typeof token !== "string") return false;
      return ["*", "**", "***", "`", "``", "__", "~~"].includes(token);
    },
    isCodeType(token?: unknown): boolean {
      if (typeof token !== "string") return false;
      return ["`", "``", "```"].includes(token);
    },
  };

  return modes.main();
};

export interface MarkdownProps {
  label?: React.ReactNode;
  newLines?: boolean;
  all?: boolean;
  strong?: string | boolean;
  em?: string | boolean;
  code?: string | boolean;
  u?: string | boolean;
  del?: string | boolean;
  a?: string | boolean;
  pre?: string | boolean;
  headers?: string | boolean;
  h1?: string | boolean;
  h2?: string | boolean;
  h3?: string | boolean;
  h4?: string | boolean;
  h5?: string | boolean;
  h6?: string | boolean;
  elementProps?: {
    strong?: HTMLElement;
    em?: HTMLElement;
    code?: HTMLElement;
    u?: HTMLSpanElement;
    del?: HTMLElement;
    a?: HTMLAnchorElement;
    pre?: HTMLPreElement;
    h1?: HTMLSpanElement;
    h2?: HTMLSpanElement;
    h3?: HTMLSpanElement;
    h4?: HTMLSpanElement;
    h5?: HTMLSpanElement;
    h6?: HTMLSpanElement;
  };
}

export const Markdown = memo(
  ({
    label,
    newLines,
    all = false,
    strong = true,
    em = true,
    code = true,
    u = true,
    del = true,
    a = true,
    pre,
    headers,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    elementProps,
  }: MarkdownProps) => {
    if (typeof label !== "string") return label;

    newLines ??= all;
    pre ??= all;
    headers ??= all;
    h1 ??= headers;
    h2 ??= headers;
    h3 ??= headers;
    h4 ??= headers;
    h5 ??= headers;
    h6 ??= headers;

    return parseMarkdown(
      label,
      { strong, em, code, u, del, a, pre, h1, h2, h3, h4, h5, h6 },
      { ...elementProps, a: { target: "_blank", ...elementProps?.a } },
      { newLines }
    );
  }
);
